import React, { useState } from 'react';
import { getImageFullUrlBasedonPath, showBigImage, hideImage } from '../../helper';
import { ImageIcon } from '../../../bombiix-icon';
import type { BombiixCellRendererParams } from '../types';

/** Resolve an image path to a URL (http passthrough, else the DAM/S3 resolver); empty when unresolvable. */
const resolveImageUrl = (path: string): string => {
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  return getImageFullUrlBasedonPath(path) || '';
};

/**
 * Thumbnail cell with hover-zoom via the shared `showBigImage`/`hideImage` helpers.
 * The image path comes from the column's value (`field`/`valueGetter`); use the
 * column's `getCellHidden` to suppress the cell on rows that carry no image.
 * When the row has no image (or the image fails to load) it renders the Figma
 * placeholder card (32×32 pale tile with a 20×20 `ImageIcon`) instead of a thumbnail.
 */
const ImageCell: React.FC<BombiixCellRendererParams> = ({ value }) => {
  const [errored, setErrored] = useState(false);
  const path = typeof value === 'string' ? value : '';
  const src = errored ? '' : resolveImageUrl(path);

  /**
   * Swaps to the placeholder when the thumbnail fails. The open hover preview is closed first:
   * the swap unmounts the hovered figure, so its mouseleave never fires to close it.
   * @returns Nothing.
   */
  const handleImageError = (): void => {
    hideImage();
    setErrored(true);
  };

  if (!src) {
    return (
      <div className="cag-image-cell">
        <div className="cag-image-cell__placeholder">
          <ImageIcon size={20} />
        </div>
      </div>
    );
  }

  return (
    <div className="image-detail hover-right cag-image-cell">
      <figure onMouseOver={(event) => showBigImage(event)} onMouseLeave={() => hideImage()} className="m-0">
        <img alt="product" src={src} className="cag-image-cell__img" loading="lazy" onError={handleImageError} />
      </figure>
    </div>
  );
};

export default ImageCell;
